/**
 * RouteGuard — auth + role/tier gating for a route or layout subtree.
 *
 * Consolidates the historic `<Protected …>` inline component into a
 * single named guard that works two ways:
 *
 *   1) As a wrapper around a single child (drop-in replacement for
 *      `<Protected>`):
 *          <RouteGuard><Dashboard /></RouteGuard>
 *
 *   2) As a layout guard rendering `<Outlet />` for nested routes:
 *          <Route element={<RouteGuard adminOnly><AppLayout /></RouteGuard>}>
 *              <Route index element={<Admin />} />
 *              <Route path="settings" element={<AdminSettings />} />
 *          </Route>
 *
 * Props:
 *   • adminOnly       — only role === "admin" may enter, else /app
 *   • brokerOnly      — only role === "broker" may enter, else /app
 *   • redirectAdmin   — admin gets bounced to /admin (used on retail-only
 *                       screens like /app dashboard and /app/subscription)
 *   • requireVerified — email must be verified (default: true, matches
 *                       legacy behaviour). Set false for signup-flow
 *                       screens that must remain reachable pre-verify.
 *   • children        — optional; when omitted the guard renders <Outlet />
 *                       so it can wrap nested routes.
 *
 * Loading + logged-out states are identical to the legacy Protected: a
 * centered "Loading…" splash while AuthContext is hydrating, and a
 * <Navigate to="/login"> when unauthenticated (preserving the original
 * pathname via location state so the login page can bounce back).
 */
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import VerifyBlock from "@/components/VerifyBlock";

export default function RouteGuard({
    children,
    adminOnly = false,
    brokerOnly = false,
    redirectAdmin = false,
    requireVerified = true,
}) {
    const { user } = useAuth();
    const loc = useLocation();

    // AuthContext is still hydrating from cookie/JWT — show a skeleton
    // rather than flashing the login page.
    if (user === null) {
        return (
            <div
                className="min-h-screen flex items-center justify-center text-muted-foreground"
                data-testid="route-guard-loading"
            >
                Loading…
            </div>
        );
    }

    if (user === false) {
        return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
    }

    if (adminOnly && user.role !== "admin") {
        return <Navigate to="/app" replace />;
    }

    if (brokerOnly && user.role !== "broker") {
        return <Navigate to="/app" replace />;
    }

    // Super Admin doesn't trade and doesn't pay — retail-only screens
    // bounce admins back to /admin.
    if (redirectAdmin && user.role === "admin") {
        return <Navigate to="/admin" replace />;
    }

    // Email-verification wall — skipped for admin/broker (they're
    // pre-verified by the seed / broker approval workflow).
    if (requireVerified && !user.is_email_verified && !adminOnly && !brokerOnly) {
        return <VerifyBlock />;
    }

    return children ?? <Outlet />;
}
